'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/lib/store/auth-store'
import { useMyUserLeagues, joinUserLeague } from '@/lib/api/user-league-routes'
import { Spinner } from '@/components/ui/spinner'

function MyLeaguesList({ token }: { token: string }) {
  const { data: leagues, isLoading } = useMyUserLeagues(token)
  const router = useRouter()

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-5 text-white/50" />
      </div>
    )
  }

  if (!leagues || leagues.length === 0) {
    return <p className="py-6 text-center text-sm text-white/40">You're not in any leagues yet.</p>
  }

  return (
    <div className="flex flex-col gap-2">
      {leagues.map((league) => (
        <button
          key={league.id}
          onClick={() => router.push(`/user-leagues/${league.id}`)}
          className="flex items-center justify-between rounded-lg border border-white/8 bg-[#0e1114] px-4 py-3 text-left transition-colors hover:border-white/20 hover:bg-white/5"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{league.name}</p>
            <p className="mt-0.5 text-xs text-white/40">
              {league.member_count} {league.member_count === 1 ? 'member' : 'members'}
              {league.is_admin && <span className="ml-1.5 text-amber-400">· admin</span>}
            </p>
          </div>
          {league.league_code && (
            <span className="shrink-0 rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-xs text-white/60">
              {league.league_code}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

function JoinLeagueBox({ token }: { token: string | undefined }) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault()
    if (!token) {
      setError('Sign in to join a league.')
      return
    }
    if (!code.trim()) return

    setJoining(true)
    setError(null)
    try {
      const league = await joinUserLeague(token, code.trim())
      queryClient.invalidateQueries({ queryKey: ['myUserLeagues'] })
      router.push(`/user-leagues/${league.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join league.')
      setJoining(false)
    }
  }

  return (
    <div className="rounded-lg border border-white/8 bg-[#0e1114] p-4">
      <p className="mb-1 text-sm font-semibold text-white">Have a join code?</p>
      <p className="mb-3 text-xs text-white/40">Enter it below to join someone else's league.</p>
      <form onSubmit={handleJoin} className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. KW6H4T"
          maxLength={20}
          className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm text-white placeholder:text-white/25 outline-none focus:border-white/25 transition-colors"
        />
        <button
          type="submit"
          disabled={joining || !code.trim()}
          className="shrink-0 rounded-md bg-white px-4 py-2 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {joining ? (
            <span className="flex items-center gap-1.5">
              <Spinner className="size-3.5" />
              Joining…
            </span>
          ) : (
            'Join'
          )}
        </button>
      </form>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  )
}

export function UserLeaguesPage() {
  const router = useRouter()
  const { session } = useAuthStore()
  const token = session?.access_token

  return (
    <div className="w-full pt-8 pb-16">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-lg border border-white/8 overflow-hidden" style={{ background: '#0a0c0e' }}>
          {/* Intro */}
          <div className="px-5 py-5 border-b border-white/8">
            <h1 className="text-base font-semibold text-white">User Leagues</h1>
            <p className="mt-2 text-sm text-white/50 leading-relaxed">
              Create a private league and invite friends with a join code. Pick which competitions count,
              set a start and end date, and everyone's predictions in that window are scored automatically —
              3 points for the correct outcome, +1 for each team's goal tally you get right, and a bonus point
              for nailing the exact scoreline. Miss a prediction and it defaults to a 1-1 guess.
            </p>
          </div>

          <div className="px-5 py-5 border-b border-white/8">
            <button
              onClick={() => router.push('/create-leagues')}
              className="w-full rounded-md bg-white py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
            >
              Create a league
            </button>
          </div>

          {/* Join */}
          <div className="px-5 py-5 border-b border-white/8">
            <JoinLeagueBox token={token} />
          </div>

          {/* My leagues */}
          <div className="px-5 py-5">
            <p className="mb-3 text-xs font-medium text-white/35 uppercase tracking-wider">Your leagues</p>
            {token ? (
              <MyLeaguesList token={token} />
            ) : (
              <p className="py-6 text-center text-sm text-white/40">Sign in to see leagues you're a part of.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
